import React, { useState, useEffect } from 'react';
import { auth, googleProvider } from '../firebase/config';
import { signInWithPopup, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { Link } from 'react-router-dom';
import {
  Button,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import GoogleIcon from '@mui/icons-material/Google';
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import { useDraft } from '../contexts/DraftContext';
import firebaseService from '../firebase/firebaseService';

const AuthContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1)
}));

const UserInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  cursor: 'pointer',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#4CAF50',
  color: 'white',
  '&:hover': {
    backgroundColor: '#388E3C'
  }
}));

const Auth = () => {
  const { getDrafts } = useDraft();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [error, setError] = useState(null);
  const [showSignInDialog, setShowSignInDialog] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setUser(user);
      
      if (user) {
        try {
          // Check if user is admin
          const adminStatus = await firebaseService.isUserAdmin(user.uid);
          setIsAdmin(adminStatus);
          
          // Load drafts
          await getDrafts();
        } catch (error) {
          console.error('Error checking admin status or loading drafts:', error);
        }
      }
      
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, [getDrafts]);

  const handleSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      await signInWithPopup(auth, googleProvider);
      setShowSignInDialog(false);
    } catch (error) {
      console.error('Error signing in:', error);
      let errorMessage = 'Failed to sign in with Google. Please try again.';
      
      // Handle specific error cases
      switch (error.code) {
        case 'auth/popup-blocked':
          errorMessage = 'The sign-in popup was blocked. Please allow popups for this site and try again.';
          break;
        case 'auth/popup-closed-by-user':
          errorMessage = 'The sign-in popup was closed. Please try again.';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = 'Multiple popup requests were made. Please try again.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'A network error occurred. Please check your connection and try again.';
          break;
        default:
          if (error.message) {
            errorMessage = error.message;
          }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
      setShowSignInDialog(false);
      setEmail('');
      setPassword('');
    } catch (error) {
      console.error('Error signing in:', error);
      let errorMessage = 'Invalid email or password. Please try again.';
      
      // Handle specific error cases
      switch (error.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          errorMessage = 'Invalid email or password. Please try again.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email format.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed attempts. Please try again later.';
          break;
        default:
          if (error.message) {
            errorMessage = error.message;
          }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      setError(null);
      await signOut(auth);
      handleClose();
      // Redirect to home page after signing out
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (loading) {
    return (
      <AuthContainer>
        <CircularProgress size={24} />
      </AuthContainer>
    );
  }

  if (user) {
    return (
      <AuthContainer>
        <IconButton onClick={handleClick} sx={{ color: '#fff' }}>
          <MenuIcon />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem disabled sx={{ opacity: '1 !important', pointerEvents: 'none' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {user.displayName || user.email}
            </Typography>
          </MenuItem>
          {isAdmin && (
            <MenuItem component={Link} to="/admin" onClick={handleClose}>
              Admin Panel
            </MenuItem>
          )}
          <MenuItem component={Link} to="/drafts" onClick={handleClose}>
            My Drafts
          </MenuItem>
          <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
        </Menu>
      </AuthContainer>
    );
  }

  return (
    <>
      <AuthContainer>
        <Button
          variant="contained"
          onClick={() => setShowSignInDialog(true)}
          startIcon={<GoogleIcon />}
        >
          Sign In
        </Button>
      </AuthContainer>

      <Dialog open={showSignInDialog} onClose={() => setShowSignInDialog(false)}>
        <DialogTitle>Sign In</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleEmailSignIn} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
            />
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSignInDialog(false)}>Cancel</Button>
          <Button onClick={handleSignIn} variant="contained" color="primary">
            Sign in with Google
          </Button>
          <Button onClick={handleEmailSignIn} variant="contained" color="primary">
            Sign in with Email
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Auth; 